import { Caption1, Link, Text, makeStyles } from '@fluentui/react-components';
import { getStepType, formatRecordedCalendarDate } from '@vigeo/shared';
import { tokens } from '@vigeo/ui';
import type { StepReviewProps, StepUiModule } from '../contract.js';

const useStyles = makeStyles({
  block: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
});

const kernel = getStepType('hr-record');

function HrRecordReview({ config, submission }: StepReviewProps) {
  const styles = useStyles();
  const settings = kernel.configSchema.safeParse(config);
  const answer = kernel.submissionSchema.safeParse(submission);

  return (
    <div className={styles.block}>
      <Text weight="semibold">{settings.success ? settings.data.heading : 'Recorded by our office'}</Text>
      {answer.success ? (
        <>
          <Text>Recorded as happening on {formatRecordedCalendarDate(answer.data.occurredAt)}</Text>
          {answer.data.reference === '' ? null : <Caption1>Reference: {answer.data.reference}</Caption1>}
          {answer.data.performedBy === '' ? null : <Caption1>Performed by: {answer.data.performedBy}</Caption1>}
          <Caption1>Recorded by: {answer.data.recordedBy}</Caption1>
          {answer.data.source === '' ? null : <Caption1>Source: {answer.data.source}</Caption1>}
          {settings.success && settings.data.produces === 'verification' ? <Caption1>Result: {answer.data.outcome}</Caption1> : null}
          {answer.data.note === '' ? null : <Caption1>Notes: {answer.data.note}</Caption1>}
          {/* An external link is labelled as one (ADR-009 Q4): it points outside the app, at a system
              of record Vigeo does not hold. An uploaded file is read only through the person's Files. */}
          {answer.data.evidenceUrl === null ? null : <Caption1>External link: <Link href={answer.data.evidenceUrl} target="_blank" rel="noopener noreferrer">supporting evidence</Link></Caption1>}
          {answer.data.evidenceUploadId === undefined ? null : <Caption1>Uploaded file: stored with the person’s files once scanned clean</Caption1>}
          {answer.data.credentialDetails === undefined ? null : <>
            <Caption1>Credential: {answer.data.credentialDetails.credentialType || 'Type not recorded'} · {answer.data.credentialDetails.issuingState || 'Jurisdiction not recorded'}</Caption1>
            <Caption1>Verification: {answer.data.credentialDetails.verificationStatus} · {answer.data.credentialDetails.verificationSource} · {answer.data.credentialDetails.verifiedBy}</Caption1>
            {answer.data.credentialDetails.verifiedAt === null ? null : <Caption1>Verified on {new Date(answer.data.credentialDetails.verifiedAt).toLocaleDateString()}</Caption1>}
            {answer.data.credentialDetails.verificationNote === '' ? null : <Caption1>{answer.data.credentialDetails.verificationNote}</Caption1>}
            {answer.data.credentialDetails.originalIssuedDate === '' ? null : <Caption1>Original issue date text: {answer.data.credentialDetails.originalIssuedDate}</Caption1>}
            {answer.data.credentialDetails.originalExpiryDate === '' ? null : <Caption1>Original expiry date text: {answer.data.credentialDetails.originalExpiryDate}</Caption1>}
          </>}
          {answer.data.competency?.map((assessment) => <Caption1 key={`${assessment.jobProfileKey}:${assessment.formId}`}>
            {assessment.jobProfileKey} · {assessment.assessmentType} · {assessment.evidenceUrl === undefined
              ? <>{assessment.formId} (uploaded file)</>
              : <>{assessment.formId} (external link: <Link href={assessment.evidenceUrl} target="_blank" rel="noopener noreferrer">open</Link>)</>} · {assessment.evaluator}, {assessment.evaluatorTitle}
          </Caption1>)}
          {answer.data.expiresAt === null ? null : (
            <Caption1>Expires {new Date(answer.data.expiresAt).toLocaleDateString()}</Caption1>
          )}
        </>
      ) : (
        <Text>Nothing recorded yet.</Text>
      )}
    </div>
  );
}

/**
 * `portal: null` is the whole point (ADR-005).
 *
 * There is nothing for the candidate to do here — HR ran the orientation, or received the
 * background check result. `PortalStepRenderer` already renders this case as "recorded by
 * our office", which is the truth. A portal component that threw, or one that asked the
 * person for something they cannot provide, would both be worse.
 */
export const hrRecordUi: StepUiModule = { portal: null, review: HrRecordReview };
